import { useCallback, useMemo } from "react";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  UatProject,
  UatProjectCreate,
  UatProjectUpdate,
  UatProjectSync,
  UatProjectOrder,
} from "../lusca-search-api/generated-src";
import { UatProjectsApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";

const QUERY_KEY = ["uat-projects"];

export default function useUatProjects(expandedProjectId?: string) {
  const queryClient = useQueryClient();
  const api = useMemo(() => UatProjectsApiFactory(), []);

  const projectsQuery = useQuery<UatProject[], Error>(QUERY_KEY, async () => {
    const response = await api.listUatProjects();
    return response.data.projects;
  });

  const projectOrdersQueries = useQueries({
    queries: (projectsQuery.data ?? []).map((project) => ({
      queryKey: [...QUERY_KEY, project.id, "orders"],
      queryFn: async (): Promise<UatProjectOrder[]> => {
        const orders: UatProjectOrder[] = [];
        let offset = 0;
        do {
          const response = await api.listUatProjectOrders(
            project.id,
            100,
            offset
          );
          orders.push(...response.data.orders);
          const nextOffset = response.data.nextOffset;
          if (nextOffset === undefined) {
            return orders;
          }
          if (nextOffset <= offset) {
            throw new Error("UAT Project order pagination did not advance");
          }
          offset = nextOffset;
        } while (true);
      },
      enabled:
        expandedProjectId === project.id && (project.orderCount ?? 0) > 0,
    })),
  });
  const projectOrders = (projectsQuery.data ?? []).reduce<
    Record<string, UatProjectOrder[]>
  >((ordersByProject, project, index) => {
    ordersByProject[project.id] = projectOrdersQueries[index]?.data ?? [];
    return ordersByProject;
  }, {});
  const projectOrderStates = (projectsQuery.data ?? []).reduce<
    Record<string, { isLoading: boolean; error: Error | null }>
  >((statesByProject, project, index) => {
    const query = projectOrdersQueries[index];
    statesByProject[project.id] = {
      isLoading: query?.isLoading ?? false,
      error: query?.error instanceof Error ? query.error : null,
    };
    return statesByProject;
  }, {});

  const createMutation = useMutation<UatProject, Error, UatProjectCreate>(
    async (project) => (await api.createUatProject(project)).data,
    {
      onSuccess: () => queryClient.invalidateQueries(QUERY_KEY),
    }
  );

  const updateMutation = useMutation<
    UatProject,
    Error,
    { projectId: string; update: UatProjectUpdate }
  >(
    async ({ projectId, update }) =>
      (await api.updateUatProject(projectId, update)).data,
    {
      onSuccess: () => queryClient.invalidateQueries(QUERY_KEY),
    }
  );

  // No list refetch here: the response already carries the sync state, so
  // the page starts polling at once, and it refreshes the list when the sync
  // finishes.
  const startSyncMutation = useMutation<UatProjectSync, Error, string>(
    async (projectId) => (await api.startUatProjectSync(projectId)).data
  );

  const getSync = useCallback(
    async (projectId: string, syncId: string): Promise<UatProjectSync> =>
      (await api.getUatProjectSync(projectId, syncId)).data,
    [api]
  );

  const refresh = useCallback(
    () => queryClient.invalidateQueries(QUERY_KEY),
    [queryClient]
  );

  return {
    projects: projectsQuery.data ?? [],
    projectOrders,
    projectOrderStates,
    isLoading: projectsQuery.isLoading,
    error: projectsQuery.error,
    createProject: createMutation.mutateAsync,
    updateProject: (projectId: string, update: UatProjectUpdate) =>
      updateMutation.mutateAsync({ projectId, update }),
    startSync: startSyncMutation.mutateAsync,
    getSync,
    refresh,
    isCreating: createMutation.isLoading,
    isUpdating: updateMutation.isLoading,
    isStartingSync: startSyncMutation.isLoading,
    createError: createMutation.error,
    updateError: updateMutation.error,
    syncError: startSyncMutation.error,
  };
}
